/* global React, Icon, Button, IconButton, Card, Field, Input, Select, Segmented, EmptyState, DataList, toast, t, fmt,
   PJ_API, PjDot, PjTimeEntryModal, pjDateFull, pjDateShort, pjMonthLabel, pjDayNames, pjHours, pjErrorToast */
// ============================================================
// PLUMME 3 — Prosjekter · Oversikt
// Uke, måned, rapporter og fakturagrunnlag. Alt leses fra den samme
// normaliserte modellen som resten av prosjektsiden; fakturautkastet lages av
// backend slik at nummerserie, mva og bokføring følger vanlig salgsflyt.
// ============================================================
function PjUkeoversikt({ model }) {
  const api = PJ_API();
  const [monday, setMonday] = useState(() => api.mondayOf(new Date()));
  const week = api.isoWeekNumber(monday);
  const days = api.weekDays(monday);
  const entries = api.entriesInRange(model.entries, days[0], days[6])
    .map((entry) => api.decorateEntry(entry, model))
    .sort((a, b) => a.date.localeCompare(b.date));
  const sum = api.totalHours(entries);
  const billable = api.totalHours(entries.filter((entry) => entry.activity && entry.activity.billing !== 'never'));
  return <div>
    <div className="row between pj-period-bar">
      <div className="row gap-10">
        <IconButton name="chevLeft" aria-label={t('pj.week.prev', 'Forrige uke')} onClick={() => setMonday(api.addDays(monday, -7))} />
        <b>{t('pj.week.label', 'Uke {week}', { week })} · {pjDateShort(days[0])} – {pjDateShort(days[6])}</b>
        <IconButton name="chevRight" aria-label={t('pj.week.next', 'Neste uke')} onClick={() => setMonday(api.addDays(monday, 7))} />
      </div>
      <Button variant="secondary" size="sm" icon="calendar" onClick={() => setMonday(api.mondayOf(new Date()))}>{t('pj.week.current', 'Denne uken')}</Button>
    </div>
    <div className="split">
      <Card>
        <b className="t-h3" style={{ display: 'block', marginBottom: 10 }}>{t('pj.overview.total', 'Sum registrert')}</b>
        <div className="t-display">{pjHours(sum)}</div>
        <div className="t-sm muted" style={{ fontWeight: 700, marginTop: 6 }}>{t('pj.overview.billable', '{hours} fakturerbart', { hours: pjHours(billable) })}</div>
      </Card>
      <Card>
        <b className="t-h3" style={{ display: 'block', marginBottom: 10 }}>{t('pj.overview.weekEntries', 'Registrerte timer i uke {week}', { week })}</b>
        {entries.length === 0
          ? <p className="t-sm muted" style={{ fontStyle: 'italic' }}>{t('pj.overview.empty', 'Ingen timer registrert i perioden')}</p>
          : <div className="col gap-8">
            {entries.map((entry) => <div key={entry.id} className="row between t-sm">
              <span>{pjDateFull(entry.date)} · {entry.project?.name} / {entry.activity?.name}</span>
              <b>{pjHours(entry.hours)}</b></div>)}
            <div className="row between" style={{ borderTop: '1px solid var(--border)', paddingTop: 8, fontWeight: 900 }}>
              <span>{t('pj.table.sum', 'Sum')}</span><span>{pjHours(sum)}</span></div>
          </div>}
      </Card>
    </div>
  </div>;
}

function PjManedsoversikt({ model }) {
  const api = PJ_API();
  const today = new Date();
  const [month, setMonth] = useState(() => ({ year: today.getFullYear(), month: today.getMonth() + 1 }));
  const [editEntry, setEditEntry] = useState(null);
  const range = api.monthRange(month.year, month.month);
  const entries = api.entriesInRange(model.entries, range.from, range.to)
    .map((entry) => api.decorateEntry(entry, model))
    .sort((a, b) => a.date.localeCompare(b.date));
  const byDay = api.hoursByDate(entries);
  const sum = api.totalHours(entries);
  const step = (delta) => setMonth((current) => {
    const next = current.month + delta;
    if (next < 1) return { year: current.year - 1, month: 12 };
    if (next > 12) return { year: current.year + 1, month: 1 };
    return { ...current, month: next };
  });
  const firstWeekday = (new Date(`${range.from}T12:00:00`).getDay() + 6) % 7;
  const daysInMonth = Number(range.to.slice(8, 10));
  const cells = Array.from({ length: firstWeekday }, () => null)
    .concat(Array.from({ length: daysInMonth }, (_, index) => index + 1));
  return <div>
    <div className="row between pj-period-bar">
      <div className="row gap-10">
        <IconButton name="chevLeft" aria-label={t('pj.month.prev', 'Forrige måned')} onClick={() => step(-1)} />
        <b>{pjMonthLabel(month.year, month.month)}</b>
        <IconButton name="chevRight" aria-label={t('pj.month.next', 'Neste måned')} onClick={() => step(1)} />
      </div>
      <b className="t-num">{pjHours(sum)}</b>
    </div>
    <div className="split">
      <Card>
        <div className="pj-cal">
          {pjDayNames().map((day) => <div key={day} className="t-xs muted pj-cal-head">{day.slice(0, 3).toUpperCase()}</div>)}
          {cells.map((day, index) => {
            if (!day) return <div key={`gap${index}`} />;
            const key = `${month.year}-${String(month.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const hours = byDay[key] || 0;
            return <div key={key} className={'pj-cal-day' + (hours > 0 ? ' has' : '')} title={hours > 0 ? t('pj.month.dayHours', '{hours} registrert', { hours: pjHours(hours) }) : undefined}>
              {String(day).padStart(2, '0')}</div>;
          })}
        </div>
      </Card>
      <Card pad={false}>
        <div style={{ padding: '16px 16px 0' }}><b className="t-h3">{t('pj.month.entries', 'Registrerte timer i {month}', { month: pjMonthLabel(month.year, month.month) })}</b></div>
        {entries.length === 0
          ? <EmptyState compact noVisual title={t('pj.overview.emptyTitle', 'Ingen timer')} text={t('pj.overview.empty', 'Ingen timer registrert i perioden')} />
          : <div className="tbl-wrap" style={{ border: 'none', marginTop: 10 }}><table className="tbl">
            <thead><tr><th>{t('pj.field.date', 'Dato')}</th><th>{t('pj.table.projectActivity', 'Prosjekt / aktivitet')}</th><th style={{ textAlign: 'right' }}>{t('pj.table.hours', 'Timer')}</th></tr></thead>
            <tbody>
              {entries.map((entry) => <tr data-app-control="true" key={entry.id} style={{ cursor: 'pointer' }} onClick={() => setEditEntry(entry)}>
                <td>{pjDateFull(entry.date)}</td>
                <td>{entry.project?.name} / {entry.activity?.name}</td>
                <td className="num">{pjHours(entry.hours)}</td></tr>)}
              <tr><td colSpan={2}><b>{t('pj.table.sum', 'Sum')}</b></td><td className="num"><b>{pjHours(sum)}</b></td></tr>
            </tbody></table></div>}
      </Card>
    </div>
    {editEntry && <PjTimeEntryModal open entry={editEntry} model={model} onClose={() => setEditEntry(null)} />}
  </div>;
}

function PjRapporter({ model }) {
  const api = PJ_API();
  const today = new Date();
  const monthStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  const [filters, setFilters] = useState(() => ({
    projectId: '', activityId: '', billing: 'all', invoiced: 'all',
    from: monthStart, to: api.monthRange(today.getFullYear(), today.getMonth() + 1).to,
  }));
  const [editEntry, setEditEntry] = useState(null);
  const patch = (next) => setFilters((current) => ({ ...current, ...next }));
  const entries = api.filterEntries(model, filters).sort((a, b) => a.date.localeCompare(b.date));
  const sum = api.totalHours(entries);
  const billable = api.totalHours(entries.filter((entry) => entry.activity && entry.activity.billing !== 'never'));
  const activities = model.activities.filter((activity) => !filters.projectId || activity.projectId === filters.projectId);
  const [busy, setBusy] = useState(false);
  const printReport = () => { toast(t('pj.report.opening', 'Åpner utskrift …'), 'info'); window.setTimeout(() => window.print(), 350); };
  // Samme PDF-motor som resultatregnskapet, men med timer som enhet.
  const downloadReport = async () => {
    if (!entries.length) { toast(t('pj.report.emptyText', 'Ingen timer i valgt periode og filter.'), 'warning'); return; }
    setBusy(true);
    try {
      if (!window.PlummeBackend?.downloadReportPdf) throw new Error(t('pj.report.pdfUnavailable', 'Rapport-PDF er ikke koblet til backend.'));
      const result = await window.PlummeBackend.downloadReportPdf(api.buildTimeReportExport(entries, {
        title: t('pj.report.pdfTitle', 'Timerapport'),
        periodLabel: `${pjDateFull(filters.from)} – ${pjDateFull(filters.to)}`,
        year: Number(String(filters.from).slice(0, 4)),
        totalLabel: t('pj.table.sum', 'Sum'),
        billableLabel: t('pj.filter.billable', 'Fakturerbar'),
      }));
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${result.pdfBase64}`;
      link.download = result.fileName;
      link.click();
    } catch (error) {
      pjErrorToast(error, 'pj.report.pdfError', 'Kunne ikke laste ned rapporten.');
    } finally { setBusy(false); }
  };
  return <div>
    <Card style={{ marginBottom: 18 }}>
      <div className="grid-2 gap-14 pj-filter-grid">
        <Field label={t('pj.field.project', 'Prosjekt')} help={t('helpTopics.project.body')} helpTitle={t('helpTopics.project.title')}>
          <Select value={filters.projectId} onChange={(event) => patch({ projectId: event.target.value, activityId: '' })}>
            <option value="">{t('pj.filter.all', 'Alle')}</option>
            {model.projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
          </Select></Field>
        <Field label={t('pj.filter.period', 'Tidsrom')} help={t('helpTopics.reportPeriod.body')} helpTitle={t('helpTopics.reportPeriod.title')}><div className="row gap-8 pj-date-range">
          <Input type="date" value={filters.from} onChange={(event) => patch({ from: event.target.value })} />
          <Input type="date" value={filters.to} onChange={(event) => patch({ to: event.target.value })} />
        </div></Field>
        <Field label={t('pj.field.activity', 'Aktivitet')} help={t('helpTopics.activity.body')} helpTitle={t('helpTopics.activity.title')}>
          <Select value={filters.activityId} onChange={(event) => patch({ activityId: event.target.value })}>
            <option value="">{t('pj.filter.all', 'Alle')}</option>
            {activities.map((activity) => <option key={activity.id} value={activity.id}>{activity.name}</option>)}
          </Select></Field>
        <Field label={t('pj.filter.billing', 'Fakturering')} help={t('helpTopics.billingChoice.body')} helpTitle={t('helpTopics.billingChoice.title')}>
          <Segmented soft value={filters.billing} onChange={(value) => patch({ billing: value })} options={[
            { value: 'all', label: t('pj.filter.all', 'Alle') },
            { value: 'billable', label: t('pj.filter.billable', 'Fakturerbar') },
            { value: 'nonBillable', label: t('pj.filter.nonBillable', 'Ikke') },
          ]} /></Field>
        <Field label={t('pj.filter.invoicedLabel', 'Fakturert')} help={t('helpTopics.projectCosts.body')} helpTitle={t('helpTopics.projectCosts.title')}>
          <Segmented soft value={filters.invoiced} onChange={(value) => patch({ invoiced: value })} options={[
            { value: 'all', label: t('pj.filter.all', 'Alle') },
            { value: 'invoiced', label: t('pj.filter.invoiced', 'Fakturert') },
            { value: 'open', label: t('pj.filter.open', 'Ufakturert') },
          ]} /></Field>
      </div>
    </Card>
    <Card pad={false}>
      <div className="row between" style={{ padding: '16px 16px 0' }}>
        <b className="t-h3">{t('pj.report.title', 'Registrerte timer {from} – {to}', { from: pjDateFull(filters.from), to: pjDateFull(filters.to) })}</b>
      </div>
      {entries.length === 0
        ? <EmptyState compact noVisual title={t('pj.report.emptyTitle', 'Ingen registreringer')} text={t('pj.report.emptyText', 'Ingen timer i valgt periode og filter.')} />
        : <div style={{ marginTop: 10 }}><DataList rows={entries} rowKey={(entry) => entry.id} paginate={false}
          onRowClick={(entry) => setEditEntry(entry)} columns={[
            { header: t('pj.field.date', 'Dato'), sort: (entry) => entry.date, render: (entry) => pjDateFull(entry.date) },
            { header: t('pj.field.activity', 'Aktivitet'), render: (entry) => entry.activity?.name || '' },
            { header: t('pj.field.project', 'Prosjekt'), render: (entry) => entry.project?.name || '' },
            { header: t('pj.field.description', 'Beskrivelse'), cellClass: 't-sm muted', render: (entry) => entry.note },
            { header: t('pj.filter.invoicedLabel', 'Fakturert'), headerRight: true, cellClass: 'num',
              render: (entry) => (entry.invoiceStatus === 'invoiced' ? t('common.yes') : '—') },
            { header: t('pj.table.hours', 'Timer'), headerRight: true, cellClass: 'num',
              sort: (entry) => Number(entry.hours) || 0, render: (entry) => pjHours(entry.hours) },
          ]}
          footer={() => <tr><td colSpan={5}><b>{t('pj.table.sum', 'Sum')}</b></td><td className="num"><b>{pjHours(sum)}</b></td></tr>} /></div>}
      <div className="row between" style={{ padding: 16, borderTop: '1px solid var(--border)', flexWrap: 'wrap', gap: 10 }}>
        <div className="row gap-10">
          <Button variant="secondary" size="sm" icon="download" disabled={busy} onClick={downloadReport}>{busy ? t('common.working') : t('pj.report.pdf', 'Last ned som PDF')}</Button>
          <Button variant="secondary" size="sm" icon="printer" onClick={printReport} data-help={t('helpTopics.exportFormat.body')} data-help-title={t('helpTopics.exportFormat.title')}>{t('pj.report.print', 'Skriv ut')}</Button>
        </div>
        <span className="t-sm muted" style={{ fontWeight: 700 }}>{t('pj.overview.billable', '{hours} fakturerbart', { hours: pjHours(billable) })}</span>
      </div>
    </Card>
    {editEntry && <PjTimeEntryModal open entry={editEntry} model={model} onClose={() => setEditEntry(null)} />}
  </div>;
}

// Slaar sammen timegrunnlaget og kostnadsgrunnlaget til en liste per prosjekt.
// Bare prosjekter med kunde kan bli en faktura — samme regel som for timene.
function pjBasisGroupsWithCosts(api, model, search) {
  const costApi = window.PlummeProjectCostLink;
  const state = window.PlummeBackend?.appState || {};
  const query = String(search || '').trim().toLowerCase();
  const groups = api.buildInvoiceBasisGroups(model, { search });
  const byProject = new Map(groups.map((group) => [group.project.id, { ...group, costs: [], costAmount: 0 }]));
  costApi.collectProjectCosts(state).forEach((cost) => {
    const project = model.projectsById.get(cost.projectId);
    if (!project || !(project.customerId || project.customerName)) return;
    if (query && !`${project.name} ${project.customerName || ''}`.toLowerCase().includes(query)) return;
    const group = byProject.get(project.id)
      || { project, entries: [], hours: 0, from: cost.date, to: cost.date, costs: [], costAmount: 0 };
    group.costs.push(cost);
    group.from = group.from && group.from < cost.date ? group.from : cost.date;
    group.to = group.to && group.to > cost.date ? group.to : cost.date;
    byProject.set(project.id, group);
  });
  return Array.from(byProject.values())
    .map((group) => ({ ...group, costAmount: costApi.totalCostAmount(group.costs) }))
    .filter((group) => group.entries.length || group.costs.length);
}

function PjFakturagrunnlagGroup({ group, open, onToggle, setRoute }) {
  const api = PJ_API();
  const costApi = window.PlummeProjectCostLink;
  const [selected, setSelected] = useState(() => new Set(group.entries.map((entry) => entry.id)));
  const [selectedCosts, setSelectedCosts] = useState(() => new Set(group.costs.map((cost) => cost.key)));
  const [timesheet, setTimesheet] = useState(true);
  const [busy, setBusy] = useState(false);
  // Bilaget må kunne åpnes her: den som viderefakturerer et utlegg skal se hva
  // kunden faktisk får på fakturaen før utkastet lages.
  const [costPreview, setCostPreview] = useState(null);
  useEffect(() => { setSelected(new Set(group.entries.map((entry) => entry.id))); }, [group.entries.map((entry) => entry.id).join(',')]);
  useEffect(() => { setSelectedCosts(new Set(group.costs.map((cost) => cost.key))); }, [group.costs.map((cost) => cost.key).join(',')]);
  const toggleSet = (setter) => (key) => setter((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggle = toggleSet(setSelected);
  const toggleCost = toggleSet(setSelectedCosts);
  const chosen = group.entries.filter((entry) => selected.has(entry.id));
  const chosenCosts = group.costs.filter((cost) => selectedCosts.has(cost.key));
  const createDraft = async () => {
    if (!chosen.length && !chosenCosts.length) { toast(t('pj.basis.pickOne', 'Velg minst én registrering'), 'warning'); return; }
    setBusy(true);
    try {
      const result = await window.PlummeBackend.createInvoiceDraftFromTimeEntries({
        projectId: group.project.id,
        entryIds: chosen.map((entry) => entry.id),
        costKeys: chosenCosts.map((cost) => cost.key),
        includeTimesheet: timesheet,
      });
      if (result?.invoice) window.PlummeBackend.setOpenInvoice?.(result.invoice);
      toast(t('pj.basis.created', 'Fakturautkast opprettet for {project}', { project: group.project.name }), 'success');
      setRoute?.('faktura');
    } catch (error) {
      pjErrorToast(error, 'pj.basis.error', 'Kunne ikke opprette fakturautkastet.');
    } finally { setBusy(false); }
  };
  return <div style={{ borderBottom: '1px solid var(--border)' }}>
    <div data-app-control="true" className="row between pj-basis-head" onClick={onToggle}>
      <div className="row gap-12">
        <PjDot color={group.project.color} />
        <div>
          <div className="row gap-8 wrap">
            <b>{group.project.name}</b>
            {group.project.customerName && <span className="pj-basis-customer">{group.project.customerName}</span>}
          </div>
          <div className="t-xs muted">{t('pj.basis.summary', '{count} ufakturerte registreringer fra {from} til {to}', {
            count: group.entries.length, from: pjDateFull(group.from), to: pjDateFull(group.to),
          })}</div>
        </div>
      </div>
      <div className="col" style={{ alignItems: 'flex-end' }}>
        <b>{pjHours(group.hours)}</b>
        {group.costAmount > 0 && <span className="t-xs muted" style={{ fontWeight: 800 }}>{fmt(group.costAmount)}</span>}
      </div>
    </div>
    {open && <div style={{ padding: '0 16px 16px' }}>
      {group.entries.length > 0 && <DataList rows={group.entries} rowKey={(entry) => entry.id} paginate={false} columns={[
        { header: '', sortable: false, render: (entry) => <input type="checkbox" checked={selected.has(entry.id)} onChange={() => toggle(entry.id)} /> },
        { header: t('pj.field.date', 'Dato'), sort: (entry) => entry.date, render: (entry) => pjDateFull(entry.date) },
        { header: t('pj.field.activity', 'Aktivitet'), render: (entry) => entry.activity?.name || '' },
        { header: t('pj.field.description', 'Beskrivelse'), cellClass: 't-sm muted', render: (entry) => entry.note },
        { header: t('pj.table.hours', 'Timer'), headerRight: true, cellClass: 'num',
          sort: (entry) => Number(entry.hours) || 0, render: (entry) => pjHours(entry.hours) },
      ]}
      footer={() => <tr><td colSpan={4}><b>{t('pj.basis.selected', 'Valgt')}</b></td><td className="num"><b>{pjHours(api.totalHours(chosen))}</b></td></tr>} />}
      {/* Utlegg viderefaktureres som vanlig salg: nettobelopet fra bilaget blir
          en salgslinje, og fakturamotoren avgjor mva som ellers. */}
      {group.costs.length > 0 && <div style={{ marginTop: group.entries.length ? 16 : 0 }}>
        <b className="t-h3" style={{ fontSize: 14 }}>{t('pj.basis.costs', 'Utlegg til viderefakturering')}</b>
        <div style={{ marginTop: 8 }}><DataList rows={group.costs} rowKey={(cost) => cost.key} paginate={false} columns={[
          { header: '', sortable: false, render: (cost) => <input type="checkbox" checked={selectedCosts.has(cost.key)} onChange={() => toggleCost(cost.key)} /> },
          { header: t('pj.field.date', 'Dato'), sort: (cost) => cost.date, render: (cost) => pjDateFull(cost.date) },
          { header: t('pj.basis.costText', 'Utlegg'), render: (cost) => cost.description },
          { header: t('pj.basis.costSource', 'Kilde'), cellClass: 't-sm muted',
            render: (cost) => (cost.kind === costApi.PROJECT_COST_EMPLOYEE_EXPENSE
              ? t('pj.basis.costEmployee', 'Ansattutlegg · {name}', { name: cost.employeeName || '' })
              : t('pj.basis.costPurchase', 'Bokført kjøp')) },
          { header: t('pj.cost.voucher', 'Bilag'), sortable: false,
            render: (cost) => (window.PjBilagButton ? <window.PjBilagButton cost={cost} onOpen={setCostPreview} /> : null) },
          { header: t('pj.basis.costAmount', 'Beløp'), headerRight: true, cellClass: 'num',
            sort: (cost) => Number(cost.amountNet) || 0, render: (cost) => fmt(cost.amountNet) },
        ]}
        footer={() => <tr><td colSpan={5}><b>{t('pj.basis.selected', 'Valgt')}</b></td><td className="num"><b>{fmt(costApi.totalCostAmount(chosenCosts))}</b></td></tr>} /></div>
      </div>}
      {costPreview && window.PjBilagModal && <window.PjBilagModal cost={costPreview} onClose={() => setCostPreview(null)} />}
      <div className="row between wrap gap-10" style={{ marginTop: 14 }}>
        <label className="row gap-8 t-sm" style={{ fontWeight: 700 }}>
          <input type="checkbox" checked={timesheet} onChange={(event) => setTimesheet(event.target.checked)} />
          {t('pj.basis.timesheet', 'Lag timeliste-vedlegg')}
        </label>
        <Button icon="fileText" disabled={busy || (!chosen.length && !chosenCosts.length)} onClick={createDraft}>
          {busy ? t('common.working') : t('pj.basis.create', 'Opprett fakturautkast')}</Button>
      </div>
    </div>}
  </div>;
}

function PjFakturagrunnlag({ model, setRoute }) {
  const api = PJ_API();
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);
  // Timegrunnlaget kommer fra domenelaget, kostnadene fra prosjektkobling-modulen.
  // Et prosjekt kan ha utlegg a fakturere uten en eneste fort time, sa de
  // prosjektene ma legges til — `buildInvoiceBasisGroups` kjenner bare timer.
  const groups = pjBasisGroupsWithCosts(api, model, search);
  const total = api.totalHours(groups.flatMap((group) => group.entries));
  const costTotal = window.PlummeProjectCostLink.totalCostAmount(groups.flatMap((group) => group.costs));
  return <div>
    <div className="row between pj-period-bar">
      <b className="t-h3">{t('pj.basis.title', 'Fakturagrunnlag')}</b>
      <div style={{ width: 240 }}><Input icon="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('pj.basis.searchPlaceholder', 'Prosjekt, kunde …')} /></div>
    </div>
    <Card pad={false}>
      <div className="row between t-xs muted pj-basis-toolbar"><span>{t('pj.basis.projectName', 'Prosjektnavn')}</span><span>{t('pj.table.hours', 'Timer')}</span></div>
      {groups.length === 0
        ? <EmptyState noVisual title={t('pj.basis.emptyTitle', 'Ingen ufakturerte timer')} text={t('pj.basis.emptyText', 'Alt er fakturert, eller det er ikke registrert fakturerbare timer på prosjekter med kunde ennå.')} />
        : groups.map((group) => <PjFakturagrunnlagGroup key={group.project.id} group={group} setRoute={setRoute}
          open={openId === group.project.id} onToggle={() => setOpenId((current) => current === group.project.id ? null : group.project.id)} />)}
      {groups.length > 0 && <div className="row between" style={{ padding: '15px 16px', fontWeight: 900 }}>
        <span>{t('pj.table.sum', 'Sum')}</span><span>{pjHours(total)}{costTotal > 0 ? ` · ${fmt(costTotal)}` : ''}</span></div>}
    </Card>
  </div>;
}

function PjOversiktTab({ model, setRoute }) {
  const [sub, setSub] = useState('uke');
  return <div>
    <div className="row between" style={{ marginBottom: 18 }}><h2 className="t-h1">{t('pj.overview.title', 'Timeoversikt')}</h2></div>
    <Segmented value={sub} onChange={setSub} options={[
      { value: 'uke', label: t('pj.overview.week', 'Ukeoversikt') },
      { value: 'maned', label: t('pj.overview.month', 'Månedsoversikt') },
      { value: 'rapporter', label: t('pj.overview.reports', 'Rapporter') },
      { value: 'utlegg', label: t('pj.cost.tab', 'Utlegg') },
      { value: 'fakt', label: t('pj.basis.title', 'Fakturagrunnlag') },
    ]} />
    <div style={{ marginTop: 18 }}>
      {sub === 'uke' && <PjUkeoversikt model={model} />}
      {sub === 'maned' && <PjManedsoversikt model={model} />}
      {sub === 'rapporter' && <PjRapporter model={model} />}
      {sub === 'utlegg' && window.PjUtlegg && <window.PjUtlegg model={model} />}
      {sub === 'fakt' && <PjFakturagrunnlag model={model} setRoute={setRoute} />}
    </div>
  </div>;
}

Object.assign(window, { PjOversiktTab });
window.dispatchEvent?.(new CustomEvent('plumme-page-registered', { detail: { pages: ['PjOversiktTab'] } }));
